import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const ROLES = [
  { value: "composer", label: "Composer" },
  { value: "songwriter", label: "Songwriter" },
  { value: "both", label: "Both" },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const nameRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "composer",
    country: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);
  const [err, setErr] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    const res = await register(form);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome to the society");
      nav("/dashboard");
    } else {
      setErr(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24 bg-[#0A0C10] grain-bg">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="block text-center overline mb-3" data-testid="back-home">
          ← Back to home
        </Link>
        <div className="glass-card p-10">
          <h1 className="font-display text-4xl text-center">Join the society</h1>
          <p className="text-slate-400 text-center mt-2 text-sm">
            Reserved for composers, songwriters, and rights professionals.
          </p>
          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <label className="overline block mb-2">Full name</label>
              <input
                ref={nameRef}
                required
                className="input-dark"
                value={form.name}
                onChange={update("name")}
                placeholder="Élise Marchand"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <label className="overline block mb-2">Email</label>
              <input
                type="email"
                required
                className="input-dark"
                value={form.email}
                onChange={update("email")}
                placeholder="composer@example.com"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <label className="overline block mb-2">Password</label>
              <input
                type="password"
                required
                minLength={6}
                className="input-dark"
                value={form.password}
                onChange={update("password")}
                placeholder="At least 6 characters"
                data-testid="register-password-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-2">Role</label>
                <select
                  className="input-dark"
                  value={form.role}
                  onChange={update("role")}
                  data-testid="register-role-select"
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value} className="bg-[#0A0C10]">
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="overline block mb-2">Country</label>
                <input
                  className="input-dark"
                  value={form.country}
                  onChange={update("country")}
                  placeholder="France"
                  data-testid="register-country-input"
                />
              </div>
            </div>
            {err && (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2" data-testid="register-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="btn-gold w-full disabled:opacity-50"
              data-testid="register-submit-btn"
            >
              {loading ? "Creating account…" : "Create my account"}
            </button>
            <p className="text-[11px] text-slate-500 text-center mt-3 leading-relaxed">
              By creating an account you agree to our{" "}
              <Link to="/legal/terms" className="underline hover:text-slate-300">Terms</Link>,{" "}
              <Link to="/legal/privacy" className="underline hover:text-slate-300">Privacy Policy</Link>, and{" "}
              <Link to="/legal/disclaimer" className="underline hover:text-slate-300">Disclaimer</Link>.
            </p>
          </form>
          <p className="text-center text-sm text-slate-400 mt-6">
            Already a member?{" "}
            <Link to="/login" className="text-[#7DA3D9] hover:text-[#B8D0EE]" data-testid="goto-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
